import { useCallback, useMemo, useRef, useState } from 'react'
import { useChat } from '@ai-sdk/react'
import type { UIMessage } from 'ai'

import {
  AiChatDataPart,
  AiChatDebugEvent,
  AiChatDebugTurn,
  AiChatMessage,
  UseAiChatConfig,
  UseAiChatResult,
} from '../types/aiChat.types'
import { createBackendChatTransport } from '../transport/createBackendChatTransport'

const DATA_PREFIX = 'data-'

// The mutable form of AiChatDebugTurn, appended to as the turn streams.
type RecordedTurn = {
  question: string
  askedAt: string
  events: AiChatDebugEvent[]
}

const toDomainMessage = (message: UIMessage): AiChatMessage => {
  let text = ''
  const data: AiChatDataPart[] = []

  message.parts.forEach((part) => {
    if (part.type === 'text') {
      text += part.text
    } else if (part.type.startsWith(DATA_PREFIX)) {
      data.push({
        name: part.type.slice(DATA_PREFIX.length),
        data: (part as { data: unknown }).data,
      })
    }
  })

  return { id: message.id, role: message.role, text, data }
}

/** Domain-facing chat hook: wraps `useChat` + the backend transport and returns a
 * framework-neutral view, keeping the AI SDK wrapped inside the lib. */
export function useAiChat({
  onError,
  errorCopy,
  defaultErrorMessage,
  ...transportConfig
}: UseAiChatConfig): UseAiChatResult {
  // Held outside React state: the trace is only read on demand, so appending an
  // event must not re-render the conversation on every chunk.
  const traceRef = useRef<RecordedTurn[]>([])

  // Built once — the base path is fixed per usage site.
  const [transport] = useState(() =>
    createBackendChatTransport({
      ...transportConfig,
      onEvent: (event) => {
        traceRef.current[traceRef.current.length - 1]?.events.push({
          at: new Date().toISOString(),
          event,
        })
      },
    })
  )
  const [phase, setPhase] = useState<string | undefined>(undefined)

  const chat = useChat({
    transport,
    onError,
    // Transient data-status parts carry the live phase (delivered here, not
    // persisted onto the message), so track the latest for the UI.
    onData: (dataPart) => {
      if (dataPart.type === 'data-status') {
        setPhase((dataPart as { data?: { phase?: string } }).data?.phase)
      }
    },
  })

  const messages = useMemo(
    () => chat.messages.map(toDomainMessage),
    [chat.messages]
  )

  const getDebugTrace = useCallback(
    (): readonly AiChatDebugTurn[] => traceRef.current,
    []
  )

  return {
    messages,
    status: chat.status,
    phase,
    errorMessage: chat.error
      ? errorCopy?.[chat.error.message] ??
        defaultErrorMessage ??
        chat.error.message
      : undefined,
    ask: (question: string) => {
      setPhase(undefined)
      traceRef.current.push({
        question,
        askedAt: new Date().toISOString(),
        events: [],
      })
      void chat.sendMessage({ text: question })
    },
    stop: chat.stop,
    getDebugTrace,
  }
}
